/**
 * CSV Validation Utilities for Data Adjustments
 * 
 * Provides comprehensive CSV file validation including:
 * - Empty file detection
 * - Minimum row requirements
 * - Column count validation
 * - UTC column presence
 * - Formula injection detection
 */

import type { TFunction } from 'i18next';

/**
 * Auto-detect CSV separator from the first line
 * Supports: semicolon (;), comma (,), and tab (\t)
 */
export const detectCSVSeparator = (firstLine: string): string => {
  if (firstLine.includes(',') && !firstLine.includes(';')) return ',';
  if (firstLine.includes('\t')) return '\t';
  if (firstLine.includes('|') && !firstLine.includes(';') && !firstLine.includes(',')) return '|';
  return ';'; // Default to semicolon for German locale
};

/**
 * Validates CSV file format before processing
 * Returns error message if validation fails, null if valid
 * 
 * Validation checks:
 * 1. Empty file check
 * 2. Minimum rows check (header + at least 1 data row)
 * 3. Column count validation (at least 2 columns)
 * 4. UTC column presence
 * 5. Valid data rows existence
 * 6. Formula injection detection
 */
export const validateCSVFile = async (
  file: File,
  t: TFunction
): Promise<string | null> => {
  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const text = e.target?.result as string;

      // Validation 1: Empty file
      if (!text || text.trim() === '') {
        resolve(t('errors.fileIsEmpty'));
        return;
      }

      const lines = text.split(/\r?\n/).filter(Boolean);

      // Validation 2: Minimum 2 lines (header + at least 1 data row)
      if (lines.length < 2) {
        resolve(t('errors.notEnoughDataRows'));
        return;
      }

      // Auto-detect separator
      const sep = detectCSVSeparator(lines[0]);
      const headers = lines[0].split(sep).map((h) => h.trim());

      // Validation 3: Must have at least 2 columns (UTC + at least one value column)
      if (headers.length < 2) {
        resolve(t('errors.invalidColumnCount', { count: headers.length }));
        return;
      }

      // Validation 4: UTC column must be present
      const utcIndex = headers.findIndex((h) => h.toLowerCase() === 'utc');
      if (utcIndex === -1) {
        resolve(t('errors.missingUtcColumn'));
        return;
      }

      // Validation 5: Must have at least 1 valid data row
      const validDataRows = lines.slice(1).filter((line) => {
        const parts = line.split(sep);
        // Check if row has data and UTC value exists
        return parts.length >= 2 && parts[utcIndex]?.trim();
      });

      if (validDataRows.length === 0) {
        resolve(t('errors.noValidDataRows'));
        return;
      }

      // Validation 6: Formula Injection Detection (CSV Injection)
      // Check all cells for potential formula injection
      // Note: We need to allow negative numbers like -100, -5.5 but block formulas
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const cells = line.split(sep);

        for (let j = 0; j < cells.length; j++) {
          const cell = cells[j].trim();

          // Skip empty cells
          if (!cell) continue;

          const firstChar = cell.charAt(0);

          // Always block = and @ (definite formula injection)
          if (firstChar === '=' || firstChar === '@') {
            const lineNumber = i + 1; // 1-indexed for user readability
            const columnName = headers[j] || `Column ${j + 1}`;

            resolve(
              t('errors.formulaInjection', {
                line: lineNumber,
                column: columnName,
                char: firstChar,
                value: cell.substring(0, 20) + (cell.length > 20 ? '...' : ''),
              })
            );
            return;
          }

          // For + and -, check if it's a valid number
          if (firstChar === '+' || firstChar === '-') {
            // Try to parse as number
            const numValue = parseFloat(cell);

            // If it's NaN or the string contains non-numeric characters after +/- (except dot and digits)
            // then it's likely a formula injection attempt
            if (isNaN(numValue) || !/^[+-]?\d+\.?\d*([eE][+-]?\d+)?$/.test(cell)) {
              const lineNumber = i + 1;
              const columnName = headers[j] || `Column ${j + 1}`;

              resolve(
                t('errors.formulaInjection', {
                  line: lineNumber,
                  column: columnName,
                  char: firstChar,
                  value: cell.substring(0, 20) + (cell.length > 20 ? '...' : ''),
                })
              );
              return;
            }
          }
        }
      }

      // All validations passed
      resolve(null);
    };

    reader.onerror = () => {
      resolve(t('errors.fileReadError', { name: file.name }));
    };

    reader.readAsText(file);
  });
};

/**
 * Validates multiple CSV files in parallel
 * 1. Runs per-file validation (validateCSVFile)
 * 2. If all pass individually, performs cross-file consistency check
 *    (column count parity)
 * Returns the first failure encountered, or null if all valid.
 */
export const validateCSVFiles = async (
  files: File[],
  t: TFunction
): Promise<{ file: File; error: string } | null> => {
  // Stage 1: per-file validation
  const validationResults = await Promise.all(
    files.map(async (file) => ({
      file,
      error: await validateCSVFile(file, t),
    }))
  );

  const failedValidation = validationResults.find((result) => result.error !== null);
  if (failedValidation && failedValidation.error) {
    return { file: failedValidation.file, error: failedValidation.error };
  }

  // Stage 2: cross-file consistency (only when >= 2 files and all individually valid)
  if (files.length < 2) return null;

  const headers: Array<{ file: File; count: number }> = await Promise.all(
    files.map(
      (file) =>
        new Promise<{ file: File; count: number }>((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const text = (e.target?.result as string) ?? '';
            const firstLine =
              text.split(/\r?\n/).find((l) => l.trim().length > 0) ?? '';
            const sep = detectCSVSeparator(firstLine);
            const count = firstLine.split(sep).length;
            resolve({ file, count });
          };
          reader.onerror = () => resolve({ file, count: 0 });
          reader.readAsText(file);
        })
    )
  );

  const baseline = headers[0];
  const mismatch = headers.find((h) => h.count !== baseline.count);
  if (mismatch) {
    return {
      file: mismatch.file,
      error: t('errors.inconsistentColumnCount', {
        file1: baseline.file.name,
        count1: baseline.count,
        file2: mismatch.file.name,
        count2: mismatch.count,
      }),
    };
  }

  return null;
};
